/**
 * North Dakota State-Specific Diagnostic Questions
 * CORRECTED VERSION - All citations verified against actual NDAC 33-03-10.1
 * 
 * Regulatory Body: North Dakota Department of Health and Human Services
 * Primary Regulation: NDAC 33-03-10.1 - Home Health Agencies
 * Enabling Statute: NDCC 23-17.3 - Home Health Agency Licensure
 * 
 * CRITICAL CORRECTION: Original file cited NDAC 33-03-24.1 throughout
 * NDAC 33-03-24.1 = BASIC CARE FACILITIES (nursing homes, assisted living)
 * NDAC 33-03-10.1 = HOME HEALTH AGENCIES (correct regulation)
 * 
 * VERIFIED NDAC 33-03-10.1 Structure:
 * - § 33-03-10.1-01: Definitions
 * - § 33-03-10.1-02: Conflict with Federal Requirements
 * - § 33-03-10.1-03: Application, Issuance, and Renewal of License
 * - § 33-03-10.1-04: Inspection by Department
 * - § 33-03-10.1-05: Plan of Correction
 * - § 33-03-10.1-06: Enforcement
 * - § 33-03-10.1-07: Order and Notice of Order
 * - § 33-03-10.1-08: Request for Reconsideration
 * - § 33-03-10.1-09: Appeals
 * - § 33-03-10.1-10: Governing Body
 * - § 33-03-10.1-11: Quality Assessment and Performance Improvement
 * - § 33-03-10.1-12: Education Programs
 * - § 33-03-10.1-13: Medical Services
 * - § 33-03-10.1-14: Nursing Services
 * - § 33-03-10.1-15: Patient Plan of Care
 * - § 33-03-10.1-16: Clinical Record Services
 * - § 33-03-10.1-17: Therapeutic Services
 * - § 33-03-10.1-18: Home Health Aide Training and Competency Evaluation
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NORTH_DAKOTA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'ND-A1',
    itemId: 'A1',
    stateCode: 'ND',
    title: 'Home Health Agency License Status',
    description: 'North Dakota requires all home health agencies to be licensed under NDCC 23-17.3',
    severity: 'high',
    regulationCode: '23-17.3 / NDAC 33-03-10.1-03',
    regulationName: 'North Dakota Home Health Agency Licensure',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from ND DHHS?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per NDAC 33-03-10.1-03, licenses expire December 31 each year and must be renewed annually.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'ND home health licenses expire December 31 annually per NDAC 33-03-10.1-03(2)',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your North Dakota Home Health Agency license is current and active per NDCC 23-17.3.',
      atRisk: 'Your license expires soon. Per NDAC 33-03-10.1-03(2), renewal applications must be received with sufficient time to process before December 31.',
      nonCompliant: 'Operating without a valid license violates NDCC 23-17.3. Per NDAC 33-03-10.1-03, you may not use the terms "home health agency" or accept patients without a license. Contact ND DHHS immediately at (701) 328-2352.',
      unknown: 'Verify your home health agency license status with ND DHHS at (701) 328-2352.',
    },
    stateNotes: [
      'Licenses expire December 31 each year (NDAC 33-03-10.1-03(2))',
      'Initial license requires on-site survey (NDAC 33-03-10.1-03(1)(a)(2))',
      'License must be displayed in conspicuous place (NDAC 33-03-10.1-03(8))',
      'Branch offices are licensed under parent agency license (NDAC 33-03-10.1-03(7))',
    ],
  },

  {
    id: 'ND-A2',
    itemId: 'A2',
    stateCode: 'ND',
    title: 'Administrator Qualifications',
    description: 'Qualified administrator requirements per NDAC 33-03-10.1-10(4)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(4)',
    regulationName: 'Administrator',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a qualified administrator designated in writing per NDAC 33-03-10.1-10(4)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Administrator must be: (1) licensed physician, RN, or bachelor\'s degree with 3 years health care management; OR (2) 6 years supervisory experience in health care management',
      },
      {
        id: 'q2',
        question: 'Which qualification does your administrator meet?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'rn', label: 'Registered nurse' },
          { value: 'bachelors', label: 'Bachelor\'s degree with 3+ years health care management' },
          { value: 'experience', label: '6+ years supervisory experience in health care management' },
          { value: 'none', label: 'None of the above' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is there an alternate administrator identified in writing per NDAC 33-03-10.1-10(4)(c)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q2', value: ['physician', 'rn', 'bachelors', 'experience'] },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Administrator qualifications meet NDAC 33-03-10.1-10(4) requirements with written alternate designated.',
      atRisk: 'No alternate administrator identified in writing. NDAC 33-03-10.1-10(4)(c) requires written designation of qualified alternate.',
      nonCompliant: 'Your agency lacks a qualified administrator per NDAC 33-03-10.1-10(4). Immediately designate a qualified administrator.',
      unknown: 'Verify administrator qualifications meet NDAC 33-03-10.1-10(4) requirements.',
    },
    stateNotes: [
      'Administrator qualifications defined in NDAC 33-03-10.1-10(4)',
      'Must be licensed physician, RN, bachelor\'s with 3 years healthcare management, or 6 years supervisory experience',
      'Written alternate administrator required per NDAC 33-03-10.1-10(4)(c)',
      'Administrator responsible for day-to-day operations',
    ],
  },

  {
    id: 'ND-A3',
    itemId: 'A3',
    stateCode: 'ND',
    title: 'Governing Body Bylaws',
    description: 'Bylaws requirements per NDAC 33-03-10.1-10(1)',
    severity: 'medium',
    regulationCode: 'NDAC 33-03-10.1-10(1)',
    regulationName: 'Governing Body Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing body have bylaws or equivalent per NDAC 33-03-10.1-10(1)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When were the bylaws last reviewed?',
        inputType: 'select',
        options: [
          { value: 'year', label: 'Within the past year' },
          { value: 'two_years', label: '1-2 years ago' },
          { value: 'older', label: 'More than 2 years ago' },
          { value: 'never', label: 'Never reviewed' },
        ],
        helpText: 'NDAC 33-03-10.1-10(1) requires bylaws to be reviewed annually',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'year', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'two_years', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['older', 'never'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body bylaws are current and reviewed annually per NDAC 33-03-10.1-10(1).',
      atRisk: 'Bylaws have not been reviewed within the past year. NDAC 33-03-10.1-10(1) requires annual review.',
      nonCompliant: 'No bylaws or bylaws severely outdated. This violates NDAC 33-03-10.1-10(1).',
      unknown: 'Review governing body bylaws requirements per NDAC 33-03-10.1-10(1).',
    },
    stateNotes: [
      'Written bylaws or equivalent required per NDAC 33-03-10.1-10(1)',
      'Annual review of bylaws required',
      'Governing body has legal authority and responsibility for agency operations',
      'Document all bylaw reviews and amendments',
    ],
  },

  {
    id: 'ND-A4',
    itemId: 'A4',
    stateCode: 'ND',
    title: 'Written Policies and Procedures',
    description: 'Policy requirements per NDAC 33-03-10.1-10(5)-(6)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(5)-(6)',
    regulationName: 'Policy and Procedure Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following required policies does your agency have per NDAC 33-03-10.1-10(5)-(6)? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Description of each service offered (33-03-10.1-10(5)(a))' },
          { value: 'procedures', label: 'Policies and procedures for each service (33-03-10.1-10(5)(b))' },
          { value: 'job_desc', label: 'Job descriptions for each discipline (33-03-10.1-10(5)(c))' },
          { value: 'records_system', label: 'Patient records maintenance system (33-03-10.1-10(5)(d))' },
          { value: 'therapeutic', label: 'Therapeutic services under physician/RN direction (33-03-10.1-10(6)(a)(1))' },
          { value: 'acceptance', label: 'Patient acceptance criteria (33-03-10.1-10(6)(a)(2))' },
          { value: 'plan_review', label: 'Plan of care review procedures (33-03-10.1-10(6)(a)(4))' },
          { value: 'confidentiality', label: 'Record confidentiality (33-03-10.1-10(6)(a)(7))' },
          { value: 'grievances', label: 'Grievance procedures (33-03-10.1-10(6)(a)(8))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets NDAC 33-03-10.1-10(5)-(6) requirements.',
      atRisk: 'Your policy manual is missing required sections per NDAC 33-03-10.1-10(5)-(6). Update before next survey.',
      nonCompliant: 'Multiple required policies are missing per NDAC 33-03-10.1-10(5)-(6). This is a common survey deficiency.',
      unknown: 'Review your policies against NDAC 33-03-10.1-10(5)-(6) requirements.',
    },
    stateNotes: [
      'Service descriptions required per NDAC 33-03-10.1-10(5)(a)',
      'Job descriptions for each discipline per NDAC 33-03-10.1-10(5)(c)',
      'Patient records maintenance system per NDAC 33-03-10.1-10(5)(d)',
      'Grievance procedures per NDAC 33-03-10.1-10(6)(a)(8)',
      'Patient acceptance criteria per NDAC 33-03-10.1-10(6)(a)(2)',
    ],
  },

  {
    id: 'ND-A5',
    itemId: 'A5',
    stateCode: 'ND',
    title: 'Patient Rights Notification',
    description: 'Patient rights requirements per NDAC 33-03-10.1-10(6)(c)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(6)(c)',
    regulationName: 'Patient Rights Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide written patient rights during the initial evaluation visit BEFORE treatment begins per NDAC 33-03-10.1-10(6)(c)?',
        inputType: 'select',
        options: [
          { value: 'before', label: 'Yes, before treatment initiation' },
          { value: 'during', label: 'During first treatment visit' },
          { value: 'later', label: 'After services have started' },
          { value: 'no', label: 'No formal process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which required patient rights are included? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'nondiscrimination', label: 'Care without discrimination (33-03-10.1-10(6)(c)(1))' },
          { value: 'exercise_rights', label: 'Right to exercise patient rights (33-03-10.1-10(6)(c)(2))' },
          { value: 'choose_provider', label: 'Choose care providers (33-03-10.1-10(6)(c)(3))' },
          { value: 'informed', label: 'Be fully informed of medical condition (33-03-10.1-10(6)(c)(4))' },
          { value: 'care_plan', label: 'Be informed of care in advance (33-03-10.1-10(6)(c)(5))' },
          { value: 'refuse', label: 'Refuse care (33-03-10.1-10(6)(c)(6))' },
          { value: 'advance_directives', label: 'Advance directives information (33-03-10.1-10(6)(c)(7))' },
          { value: 'transfer', label: 'Informed of transfer/discharge (33-03-10.1-10(6)(c)(8))' },
          { value: 'dignity', label: 'Treated with dignity, privacy, respect (33-03-10.1-10(6)(c)(9))' },
          { value: 'grievance', label: 'Voice grievances (33-03-10.1-10(6)(c)(10))' },
          { value: 'confidentiality', label: 'Confidentiality (33-03-10.1-10(6)(c)(11))' },
          { value: 'payment', label: 'Payment information (33-03-10.1-10(6)(c)(12))' },
          { value: 'hotline', label: 'DHHS toll-free hotline (33-03-10.1-10(6)(c)(13))' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'before' },
        { condition: 'countAtLeast', questionId: 'q2', value: 12 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['during', 'later'] },
        { condition: 'countAtLeast', questionId: 'q2', value: 8 },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets NDAC 33-03-10.1-10(6)(c) requirements.',
      atRisk: 'Patient rights notification timing or content may be incomplete. Must be provided BEFORE treatment per 33-03-10.1-10(6)(c).',
      nonCompliant: 'Patient rights are not properly provided. This violates NDAC 33-03-10.1-10(6)(c).',
      unknown: 'Review patient rights procedures against NDAC 33-03-10.1-10(6)(c).',
    },
    stateNotes: [
      'Written patient rights required BEFORE treatment begins per NDAC 33-03-10.1-10(6)(c)',
      'Must include DHHS toll-free hotline per NDAC 33-03-10.1-10(6)(c)(13)',
      'All 13 enumerated rights must be provided',
      'Include advance directive information per NDAC 33-03-10.1-10(6)(c)(7)',
    ],
  },

  {
    id: 'ND-A6',
    itemId: 'A6',
    stateCode: 'ND',
    title: 'Grievance Procedures',
    description: 'Grievance requirements per NDAC 33-03-10.1-10(6)(a)(8)',
    severity: 'medium',
    regulationCode: 'NDAC 33-03-10.1-10(6)(a)(8)',
    regulationName: 'Grievance Procedure Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a means to report, investigate, and document grievances per NDAC 33-03-10.1-10(6)(a)(8)?',
        inputType: 'select',
        options: [
          { value: 'complete', label: 'Yes, complete process with follow-through documentation' },
          { value: 'partial', label: 'Partial process, missing some elements' },
          { value: 'no', label: 'No grievance process' },
        ],
        required: true,
        helpText: 'Must include follow-through with patient or family',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'complete', result: 'COMPLIANT' },
      { condition: 'equals', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Grievance procedures meet NDAC 33-03-10.1-10(6)(a)(8) requirements.',
      atRisk: 'Grievance procedures are incomplete. Must include investigation, documentation, and follow-through.',
      nonCompliant: 'No grievance process in place. This violates NDAC 33-03-10.1-10(6)(a)(8).',
      unknown: 'Review grievance procedures against NDAC 33-03-10.1-10(6)(a)(8) requirements.',
    },
    stateNotes: [
      'Written grievance procedures required per NDAC 33-03-10.1-10(6)(a)(8)',
      'Must include process to report, investigate, and document grievances',
      'Follow-through with patient or family required',
      'Document all grievances and resolutions',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL
  // ============================================
  {
    id: 'ND-B1',
    itemId: 'B1',
    stateCode: 'ND',
    title: 'Personnel File Requirements',
    description: 'Personnel record requirements per NDAC 33-03-10.1-10(6)(b)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(6)(b)',
    regulationName: 'Personnel Records',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee personnel file per NDAC 33-03-10.1-10(6)(b)? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'registry_check', label: 'State registry/licensure board checks BEFORE employment (33-03-10.1-10(6)(b)(1))' },
          { value: 'job_desc', label: 'Job descriptions (33-03-10.1-10(6)(b)(2))' },
          { value: 'orientation', label: 'Orientation records (33-03-10.1-10(6)(b)(3))' },
          { value: 'training', label: 'Training and education records (33-03-10.1-10(6)(b)(4))' },
          { value: 'disciplinary', label: 'Disciplinary action records (33-03-10.1-10(6)(b)(5))' },
          { value: 'licensure', label: 'Current licensure/registration verification (33-03-10.1-10(6)(b)(6))' },
          { value: 'performance', label: 'Annual performance reviews (33-03-10.1-10(6)(b)(7))' },
          { value: 'competency', label: 'HHA competency evaluations (33-03-10.1-10(6)(b)(8))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 8, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all North Dakota-required documentation per NDAC 33-03-10.1-10(6)(b).',
      atRisk: 'Some personnel files are missing required documents. Review NDAC 33-03-10.1-10(6)(b) requirements.',
      nonCompliant: 'Personnel files are missing critical documentation required by NDAC 33-03-10.1-10(6)(b).',
      unknown: 'Audit personnel files against NDAC 33-03-10.1-10(6)(b) requirements.',
    },
    stateNotes: [
      'Registry/licensure board checks required BEFORE employment per NDAC 33-03-10.1-10(6)(b)(1)',
      'Job descriptions required per NDAC 33-03-10.1-10(6)(b)(2)',
      'Annual performance reviews per NDAC 33-03-10.1-10(6)(b)(7)',
      'HHA competency evaluations per NDAC 33-03-10.1-10(6)(b)(8)',
    ],
  },

  {
    id: 'ND-B2',
    itemId: 'B2',
    stateCode: 'ND',
    title: 'State Registry/Licensure Board Verification',
    description: 'Pre-employment registry checks per NDAC 33-03-10.1-10(6)(b)(1)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(6)(b)(1)',
    regulationName: 'Registry Verification Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you check state registries and licensure boards BEFORE employment for findings of inappropriate conduct, disciplinary actions, and termination per NDAC 33-03-10.1-10(6)(b)(1)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before employment' },
          { value: 'sometimes', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which registries/databases do you check? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'nurse_aide', label: 'ND Nurse Aide Registry' },
          { value: 'bon', label: 'ND Board of Nursing (for RNs/LPNs)' },
          { value: 'oig', label: 'OIG Exclusion List' },
          { value: 'sam', label: 'SAM Exclusion Database' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Registry verification is performed before employment per NDAC 33-03-10.1-10(6)(b)(1).',
      atRisk: 'Registry verification is inconsistent. Must be completed BEFORE employment per NDAC 33-03-10.1-10(6)(b)(1).',
      nonCompliant: 'No pre-employment registry verification. This violates NDAC 33-03-10.1-10(6)(b)(1).',
      unknown: 'Review registry verification procedures per NDAC 33-03-10.1-10(6)(b)(1).',
    },
    stateNotes: [
      'Check state registries and licensure boards BEFORE employment per NDAC 33-03-10.1-10(6)(b)(1)',
      'Check for inappropriate conduct, disciplinary actions, termination',
      'ND Nurse Aide Registry verification required',
      'Document all verification results in personnel files',
    ],
  },

  {
    id: 'ND-B3',
    itemId: 'B3',
    stateCode: 'ND',
    title: 'Nurse Executive/Director of Nursing',
    description: 'Nurse executive requirements per NDAC 33-03-10.1-14(1)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-14(1)',
    regulationName: 'Nursing Services Director Requirements',
    agencyName: 'North Dakota Board of Nursing',
    agencyAbbr: 'NDBON',
    agencyPhone: '(701) 751-3000',
    agencyUrl: 'https://www.ndbon.org/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a nurse executive/director of nursing who meets NDAC 33-03-10.1-14(1) requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Must be RN licensed in ND with 1 year direct care experience in home health and 3 years as RN',
      },
      {
        id: 'q2',
        question: 'Is the nurse executive a full-time, salaried employee per NDAC 33-03-10.1-14(1)(a)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is there an alternate RN identified in writing per NDAC 33-03-10.1-14(1)(f)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Nurse executive meets NDAC 33-03-10.1-14(1) requirements.',
      atRisk: 'Nurse executive requirements may not be fully met. Review full-time status and alternate designation.',
      nonCompliant: 'No qualified nurse executive. This violates NDAC 33-03-10.1-14(1).',
      unknown: 'Verify nurse executive qualifications per NDAC 33-03-10.1-14(1).',
    },
    stateNotes: [
      'RN licensed in ND with 1 year direct care home health and 3 years as RN per NDAC 33-03-10.1-14(1)',
      'Must be full-time, salaried employee per NDAC 33-03-10.1-14(1)(a)',
      'Written alternate RN required per NDAC 33-03-10.1-14(1)(f)',
      'Responsible for nursing services supervision',
    ],
  },

  {
    id: 'ND-B4',
    itemId: 'B4',
    stateCode: 'ND',
    title: 'RN License Verification',
    description: 'License verification requirements per NDAC 33-03-10.1-10(6)(b)(6)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(6)(b)(6) / NDCC 43-12.1',
    regulationName: 'Nursing License Verification',
    agencyName: 'North Dakota Board of Nursing',
    agencyAbbr: 'NDBON',
    agencyPhone: '(701) 751-3000',
    agencyUrl: 'https://www.ndbon.org/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify all RN/LPN licenses with NDBON before employment?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified online' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and verify renewal?',
        inputType: 'select',
        options: [
          { value: 'automated', label: 'Automated tracking with alerts' },
          { value: 'manual', label: 'Manual tracking' },
          { value: 'none', label: 'No tracking system' },
        ],
      },
      {
        id: 'q3',
        question: 'Are you aware North Dakota is a Nurse Licensure Compact (NLC) state?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Compact licenses from other NLC states are valid in ND',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['automated', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Nursing license verification meets NDAC 33-03-10.1-10(6)(b)(6) requirements.',
      atRisk: 'License verification is inconsistent. Implement systematic verification and tracking.',
      nonCompliant: 'No license verification process. Verify all licenses with NDBON at (701) 751-3000.',
      unknown: 'Review license verification procedures.',
    },
    stateNotes: [
      'Verify all RN/LPN licenses with NDBON before employment per NDAC 33-03-10.1-10(6)(b)(6)',
      'North Dakota is a Nurse Licensure Compact (NLC) state',
      'Track license expiration dates',
      'NDBON verification: (701) 751-3000',
    ],
  },

  {
    id: 'ND-B5',
    itemId: 'B5',
    stateCode: 'ND',
    title: 'Criminal Background Checks',
    description: 'Background check requirements for home health agency personnel',
    severity: 'high',
    regulationCode: '23-17.3 / NDAC 33-03-10.1-10',
    regulationName: 'Personnel Background Screening Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct criminal background checks for all staff with patient contact?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff before patient contact' },
          { value: 'most', label: 'Most staff, some pending' },
          { value: 'no', label: 'No / Not consistently' },
        ],
        required: true,
        helpText: 'Per NDCC 23-17.3: Background checks are required for personnel with patient contact.',
      },
      {
        id: 'q2',
        question: 'Do employees begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
        helpText: 'Per NDCC 23-17.3: Employees must not have patient contact until background check clearance is received.',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if staff start before clearance (direct violation - must be evaluated FIRST)
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT if not obtaining background checks
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT if all checks complete AND never start before clearance
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Criminal background check procedures meet North Dakota requirements.',
      atRisk: 'Some background checks may be incomplete or pending.',
      nonCompliant: 'VIOLATION: Staff are working before background clearance OR required checks are missing. Per NDCC 23-17.3, employees must not have patient contact until background check clearance is received. Immediately remove uncleared staff from patient care.',
      unknown: 'Verify background check procedures comply with North Dakota law.',
    },
    stateNotes: [
      'Background checks required for all patient contact personnel',
      'No patient contact allowed until clearance received',
      'Contact ND DHHS at (701) 328-2352 for guidance',
    ],
  },

  // ============================================
  // SECTION C: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'ND-C1',
    itemId: 'C1',
    stateCode: 'ND',
    title: 'Home Health Aide Training (75 Hours)',
    description: 'HHA training requirements per NDAC 33-03-10.1-18',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-18',
    regulationName: 'Home Health Aide Training and Competency Evaluation',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides have at least 75 hours of training per NDAC 33-03-10.1-18(1)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs have 75+ hours documented' },
          { value: 'most', label: 'Most have it, some in progress' },
          { value: 'no', label: 'No / Not tracking training hours' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your training program meet these requirements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'classroom_16', label: 'At least 16 hours classroom BEFORE supervised practical (33-03-10.1-18(1))' },
          { value: 'practical_16', label: 'At least 16 hours supervised practical training (33-03-10.1-18(1))' },
          { value: 'rn_supervision', label: 'Training under RN supervision with 2 years experience, 1 year in home health (33-03-10.1-18(1)(a))' },
          { value: 'required_topics', label: 'All required topics covered (33-03-10.1-18(1)(b))' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'HHA training meets 75-hour requirement per NDAC 33-03-10.1-18.',
      atRisk: 'Some HHAs may not have completed 75 hours. Review all HHA files immediately.',
      nonCompliant: 'HHAs working without required 75 hours of training. This violates NDAC 33-03-10.1-18.',
      unknown: 'Review HHA training documentation against NDAC 33-03-10.1-18.',
    },
    stateNotes: [
      '75 total hours required (NDAC 33-03-10.1-18(1))',
      'Minimum 16 hours classroom BEFORE practical training',
      'Minimum 16 hours supervised practical training',
      'Training must be supervised by RN with 2 years experience, 1 year in home health',
      'Required topics listed in NDAC 33-03-10.1-18(1)(b)',
    ],
  },

  {
    id: 'ND-C2',
    itemId: 'C2',
    stateCode: 'ND',
    title: 'HHA Competency Evaluation',
    description: 'Competency evaluation requirements per NDAC 33-03-10.1-18(2)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-18(2)',
    regulationName: 'HHA Competency Evaluation',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL HHAs have documented competency evaluations conducted by an RN per NDAC 33-03-10.1-18(2)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have RN-conducted evaluations' },
          { value: 'most', label: 'Most have them' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are the following skills evaluated by observation with a patient or live individual per NDAC 33-03-10.1-18(2)(b)? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'vitals', label: 'Temperature, pulse, respiration' },
          { value: 'hygiene', label: 'Personal hygiene and grooming' },
          { value: 'transfer', label: 'Safe transfer techniques and ambulation' },
          { value: 'positioning', label: 'Range of motion and positioning' },
        ],
        helpText: 'These 4 skills MUST be evaluated by observation, not written test',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'HHA competency evaluations meet NDAC 33-03-10.1-18(2) requirements.',
      atRisk: 'Some HHA competency evaluations may be incomplete or missing.',
      nonCompliant: 'HHAs working without proper competency evaluation. This violates NDAC 33-03-10.1-18(2).',
      unknown: 'Review HHA competency evaluation documentation.',
    },
    stateNotes: [
      'Competency evaluation conducted by RN per NDAC 33-03-10.1-18(2)',
      '4 skills must be evaluated by observation with patient or live individual per NDAC 33-03-10.1-18(2)(b)',
      'Skills observed: vitals, hygiene, transfer, positioning',
      'Document competency evaluation results in personnel files',
    ],
  },

  {
    id: 'ND-C3',
    itemId: 'C3',
    stateCode: 'ND',
    title: 'Annual In-Service Training (12 Hours)',
    description: 'Annual in-service requirements per NDAC 33-03-10.1-12(2)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-12(2)',
    regulationName: 'Annual In-Service Training',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides receive 12 hours of in-service education within each 12-month period per NDAC 33-03-10.1-12(2)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs complete 12+ hours annually' },
          { value: 'most', label: 'Most complete training, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is in-service training supervised by an RN with minimum 2 years experience, 1 year in home health per NDAC 33-03-10.1-12(2)(b)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets 12-hour requirement per NDAC 33-03-10.1-12(2).',
      atRisk: 'Some HHAs may be behind on annual training or supervision requirements not met.',
      nonCompliant: 'No structured annual in-service program. This violates NDAC 33-03-10.1-12(2).',
      unknown: 'Review annual in-service training program.',
    },
    stateNotes: [
      '12 hours required within each 12-month period',
      'May occur while aide is furnishing care to patient',
      'Must be supervised by RN with 2 years experience, 1 year in home health',
    ],
  },

  {
    id: 'ND-C4',
    itemId: 'C4',
    stateCode: 'ND',
    title: 'Annual Staff Training Requirements',
    description: 'Annual training topics per NDAC 33-03-10.1-12(1)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-12(1)',
    regulationName: 'Annual Staff Education',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL staff receive annual training on these required topics per NDAC 33-03-10.1-12(1)?',
        inputType: 'checklist',
        options: [
          { value: 'infection', label: 'Prevention and control of infections including universal precautions (33-03-10.1-12(1)(a))' },
          { value: 'rights', label: 'Patient rights (33-03-10.1-12(1)(b))' },
          { value: 'safety', label: 'Safety and emergency procedures (33-03-10.1-12(1)(c))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual staff training covers all required topics per NDAC 33-03-10.1-12(1).',
      atRisk: 'Annual training may be missing required topics. Review NDAC 33-03-10.1-12(1) requirements.',
      nonCompliant: 'Required annual training topics are not being covered. This violates NDAC 33-03-10.1-12(1).',
      unknown: 'Review annual training topics against NDAC 33-03-10.1-12(1).',
    },
    stateNotes: [
      'Annual training required for all staff per NDAC 33-03-10.1-12(1)',
      'Required topics: infection control/universal precautions per NDAC 33-03-10.1-12(1)(a)',
      'Required topics: patient rights per NDAC 33-03-10.1-12(1)(b)',
      'Required topics: safety and emergency procedures per NDAC 33-03-10.1-12(1)(c)',
    ],
  },

  // ============================================
  // SECTION D: PATIENT CARE
  // ============================================
  {
    id: 'ND-D1',
    itemId: 'D1',
    stateCode: 'ND',
    title: 'Patient Plan of Care',
    description: 'Plan of care requirements per NDAC 33-03-10.1-15',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-15',
    regulationName: 'Patient Plan of Care Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient per NDAC 33-03-10.1-15?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before services begin' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnoses', label: 'All pertinent diagnoses (33-03-10.1-15(1))' },
          { value: 'prognosis', label: 'Prognosis with short/long-term objectives (33-03-10.1-15(2))' },
          { value: 'services', label: 'Types and frequency of services (33-03-10.1-15(3))' },
          { value: 'limitations', label: 'Functional limitations (33-03-10.1-15(4))' },
          { value: 'activities', label: 'Activities permitted (33-03-10.1-15(5))' },
          { value: 'safety', label: 'Safety measures (33-03-10.1-15(6))' },
          { value: 'psychosocial', label: 'Sociopsychological needs (33-03-10.1-15(7))' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet NDAC 33-03-10.1-15 requirements.',
      atRisk: 'Plans of care may be incomplete or not created before services begin.',
      nonCompliant: 'Services are being provided without written plans of care. This violates NDAC 33-03-10.1-15.',
      unknown: 'Review plan of care process against NDAC 33-03-10.1-15 requirements.',
    },
    stateNotes: [
      'Written plan of care required for every patient per NDAC 33-03-10.1-15',
      'Must include: diagnoses, prognosis with objectives per NDAC 33-03-10.1-15(1)-(2)',
      'Must include: services, limitations, activities, safety per NDAC 33-03-10.1-15(3)-(6)',
      'Must include sociopsychological needs per NDAC 33-03-10.1-15(7)',
    ],
  },

  {
    id: 'ND-D2',
    itemId: 'D2',
    stateCode: 'ND',
    title: 'Physician Plan of Care Review',
    description: 'Physician review requirements per NDAC 33-03-10.1-10(6)(a)(4)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-10(6)(a)(4)',
    regulationName: 'Plan of Care Review Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'For patients receiving therapeutic services, is the plan of care reviewed by the physician at least every 2 months per NDAC 33-03-10.1-10(6)(a)(4)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 2 months or more frequently' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No regular physician review' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Physician plan of care review meets NDAC 33-03-10.1-10(6)(a)(4) requirements.',
      atRisk: 'Physician reviews may not be occurring every 2 months as required.',
      nonCompliant: 'No regular physician review of plans of care. This violates NDAC 33-03-10.1-10(6)(a)(4).',
      unknown: 'Review physician plan review process.',
    },
    stateNotes: [
      'Physician must review plan of care every 2 months per NDAC 33-03-10.1-10(6)(a)(4)',
      'Applies to patients receiving therapeutic services',
      'Document all physician reviews with dates and signatures',
      'More frequent reviews may be required based on patient condition',
    ],
  },

  {
    id: 'ND-D3',
    itemId: 'D3',
    stateCode: 'ND',
    title: 'RN Supervision of Home Health Aides',
    description: 'RN supervision requirements per NDAC 33-03-10.1-14(2)(a)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-14(2)(a)',
    regulationName: 'HHA Supervision Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'For patients receiving BOTH skilled nursing AND HHA services, are supervisory visits made at least every 2 weeks per NDAC 33-03-10.1-14(2)(a)(1)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 2 weeks or more frequently' },
          { value: 'usually', label: 'Usually, but sometimes less frequent' },
          { value: 'no', label: 'No - less frequently than every 2 weeks' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'For patients NOT receiving skilled nursing but receiving HHA/homemaker/companion services, does RN contact occur at least every 60 days per NDAC 33-03-10.1-14(2)(a)(2)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, every 60 days or more frequently' },
          { value: 'usually', label: 'Usually, but sometimes less frequent' },
          { value: 'no', label: 'No / not tracking this' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'always' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'equals', questionId: 'q2', value: 'usually' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'RN supervision of HHAs meets NDAC 33-03-10.1-14(2)(a) requirements.',
      atRisk: 'RN supervision may not meet required frequency. Skilled nursing + HHA: every 2 weeks; Non-skilled: every 60 days.',
      nonCompliant: 'HHAs are not receiving required RN supervision. This violates NDAC 33-03-10.1-14(2)(a).',
      unknown: 'Review RN supervision procedures.',
    },
    stateNotes: [
      'Skilled nursing + HHA: supervisory visits every 2 weeks minimum',
      'Non-skilled (HHA/homemaker/companion only): RN contact every 60 days minimum',
      'Contact includes determination of care plan appropriateness and acceptability of care',
    ],
  },

  // ============================================
  // SECTION E: CLINICAL RECORDS
  // ============================================
  {
    id: 'ND-E1',
    itemId: 'E1',
    stateCode: 'ND',
    title: 'Clinical Record Requirements',
    description: 'Clinical record requirements per NDAC 33-03-10.1-16',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-16',
    regulationName: 'Clinical Record Services',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do patient clinical records contain all required elements per NDAC 33-03-10.1-16(1)?',
        inputType: 'checklist',
        options: [
          { value: 'id', label: 'Identification data and consent forms (33-03-10.1-16(1)(a))' },
          { value: 'physician', label: 'Physician name, address, phone (33-03-10.1-16(1)(b))' },
          { value: 'orders', label: 'Signed physician orders and plan of care (33-03-10.1-16(1)(c))' },
          { value: 'assessments', label: 'Assessments and care plans by professionals (33-03-10.1-16(1)(d))' },
          { value: 'notes', label: 'Signed/dated notes documenting services (33-03-10.1-16(1)(e))' },
          { value: 'summaries', label: 'Summary reports to physician (33-03-10.1-16(1)(f))' },
          { value: 'therapeutic_orders', label: 'Diagnostic and therapeutic orders (33-03-10.1-16(1)(g))' },
          { value: 'findings', label: 'Reports of treatment and clinical findings (33-03-10.1-16(1)(h))' },
          { value: 'transfer', label: 'Transfer form if applicable (33-03-10.1-16(1)(i))' },
          { value: 'discharge', label: 'Discharge summary (33-03-10.1-16(1)(j))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per NDAC 33-03-10.1-16(1).',
      atRisk: 'Clinical records may be missing some required elements. Review documentation procedures.',
      nonCompliant: 'Clinical records do not meet NDAC 33-03-10.1-16(1) requirements.',
      unknown: 'Review clinical record documentation against NDAC 33-03-10.1-16(1).',
    },
    stateNotes: [
      'All 10 required elements must be in each record per NDAC 33-03-10.1-16(1)(a)-(j)',
      'Signed/dated notes documenting services per NDAC 33-03-10.1-16(1)(e)',
      'Discharge summary required per NDAC 33-03-10.1-16(1)(j)',
      'Records must be legible and promptly completed',
    ],
  },

  {
    id: 'ND-E2',
    itemId: 'E2',
    stateCode: 'ND',
    title: 'Record Retention',
    description: 'Record retention requirements per NDAC 33-03-10.1-16(9)',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-16(9)',
    regulationName: 'Record Retention',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain discharged patient records per NDAC 33-03-10.1-16(9)(a)?',
        inputType: 'select',
        options: [
          { value: '10plus', label: '10 years or longer from discharge' },
          { value: '7to10', label: '7-10 years' },
          { value: 'less', label: 'Less than 7 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'NDAC 33-03-10.1-16(9)(a): Live discharge = 10 years; Deceased = 7 years',
      },
      {
        id: 'q2',
        question: 'For minor patients, do you retain records for the period of minority PLUS the required years per NDAC 33-03-10.1-16(9)(b)?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, minority plus 10 years (live) or 7 years (deceased)' },
          { value: 'no', label: 'No / not tracking minor status' },
          { value: 'na', label: 'N/A - do not serve minors' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '10plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '7to10', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention policy meets NDAC 33-03-10.1-16(9) requirements (10 years from discharge).',
      atRisk: 'Record retention may not meet full requirements. ND requires 10 years from live discharge, 7 years from death.',
      nonCompliant: 'Record retention does not meet NDAC 33-03-10.1-16(9) requirements.',
      unknown: 'Review record retention requirements per NDAC 33-03-10.1-16(9).',
    },
    stateNotes: [
      'Discharged patients: 10 years from discharge date',
      'Deceased patients: 7 years',
      'Minors: Period of minority PLUS 10 years (live) or 7 years (deceased)',
    ],
  },

  {
    id: 'ND-E3',
    itemId: 'E3',
    stateCode: 'ND',
    title: 'Documentation Timeliness',
    description: 'Documentation timing requirements per NDAC 33-03-10.1-16(4)',
    severity: 'medium',
    regulationCode: 'NDAC 33-03-10.1-16(4)',
    regulationName: 'Documentation Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Are entries for services rendered written within 24 hours per NDAC 33-03-10.1-16(4)?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 24 hours' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No - often delayed beyond 24 hours' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Documentation timeliness meets NDAC 33-03-10.1-16(4) requirement (within 24 hours).',
      atRisk: 'Some documentation may be delayed beyond 24 hours. Review documentation procedures.',
      nonCompliant: 'Documentation is not completed within 24 hours as required by NDAC 33-03-10.1-16(4).',
      unknown: 'Review documentation timing procedures.',
    },
    stateNotes: [
      'Entries for services rendered must be written within 24 hours per NDAC 33-03-10.1-16(4)',
      'Include date, time, signature of person making entry',
      'Late documentation is a common survey deficiency',
      'Train staff on timely documentation requirements',
    ],
  },

  // ============================================
  // SECTION F: QUALITY & COMPLIANCE
  // ============================================
  {
    id: 'ND-F1',
    itemId: 'F1',
    stateCode: 'ND',
    title: 'Quality Assessment and Performance Improvement',
    description: 'QAPI requirements per NDAC 33-03-10.1-11',
    severity: 'high',
    regulationCode: 'NDAC 33-03-10.1-11',
    regulationName: 'QAPI',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an ongoing agencywide quality assessment program per NDAC 33-03-10.1-11(1)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your QAPI program include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'written_plan', label: 'Written plan per NDAC 33-03-10.1-11(1)(a)' },
          { value: 'identify', label: 'Mechanism to identify problems' },
          { value: 'recommend', label: 'Recommend appropriate action' },
          { value: 'implement', label: 'Implement recommendations' },
          { value: 'monitor', label: 'Monitor results' },
          { value: 'governing_reports', label: 'Findings reported to governing body per 33-03-10.1-11(1)(b)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Do you complete a performance improvement project annually per NDAC 33-03-10.1-11(2)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, annually' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets NDAC 33-03-10.1-11 requirements including annual PI project.',
      atRisk: 'QAPI program may be incomplete or missing annual PI project per NDAC 33-03-10.1-11(2).',
      nonCompliant: 'No QAPI program in place. This violates NDAC 33-03-10.1-11.',
      unknown: 'Review QAPI program against NDAC 33-03-10.1-11 requirements.',
    },
    stateNotes: [
      'Ongoing agencywide QAPI program required per NDAC 33-03-10.1-11(1)',
      'Written QAPI plan required per NDAC 33-03-10.1-11(1)(a)',
      'Report findings to governing body per NDAC 33-03-10.1-11(1)(b)',
      'Annual performance improvement project per NDAC 33-03-10.1-11(2)',
    ],
  },

  {
    id: 'ND-F2',
    itemId: 'F2',
    stateCode: 'ND',
    title: 'Contract Services Requirements',
    description: 'Contract services requirements per NDAC 33-03-10.1-10(7)',
    severity: 'medium',
    regulationCode: 'NDAC 33-03-10.1-10(7)',
    regulationName: 'Contract Services Requirements',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you contract for any services from individuals or agencies not directly employed?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your contracts include all required elements per NDAC 33-03-10.1-10(7)(a)? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'acceptance', label: 'Agency accepts patients only (33-03-10.1-10(7)(a)(1))' },
          { value: 'service', label: 'Specific service to be provided (33-03-10.1-10(7)(a)(2))' },
          { value: 'period', label: 'Contract period (33-03-10.1-10(7)(a)(3))' },
          { value: 'availability', label: 'Service availability (33-03-10.1-10(7)(a)(4))' },
          { value: 'financial', label: 'Financial arrangements (33-03-10.1-10(7)(a)(5))' },
          { value: 'licensure', label: 'Licensure verification (33-03-10.1-10(7)(a)(6))' },
          { value: 'supervision', label: 'Supervision provisions (33-03-10.1-10(7)(a)(7))' },
          { value: 'requirements', label: 'Same requirements as agency staff (33-03-10.1-10(7)(a)(8))' },
          { value: 'documentation', label: 'Documentation in patient record (33-03-10.1-10(7)(a)(9))' },
          { value: 'care_plan', label: 'Assessment/care plan sharing (33-03-10.1-10(7)(a)(10))' },
          { value: 'geographic', label: 'Geographic area (33-03-10.1-10(7)(a)(11))' },
          { value: 'billing', label: 'Agency-only billing (33-03-10.1-10(7)(a)(12))' },
          { value: 'evaluation', label: 'Service evaluation (33-03-10.1-10(7)(a)(13))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Are contracts reviewed annually per NDAC 33-03-10.1-10(7)(c)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q2', value: 12 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contract services arrangements meet NDAC 33-03-10.1-10(7) requirements.',
      atRisk: 'Some contract elements may be missing or annual review not being conducted.',
      nonCompliant: 'Contract services do not meet NDAC 33-03-10.1-10(7) requirements.',
      unknown: 'If using contract services, review against NDAC 33-03-10.1-10(7).',
    },
    stateNotes: [
      'Written contracts required for all contracted services per NDAC 33-03-10.1-10(7)',
      '13 required elements in contracts per NDAC 33-03-10.1-10(7)(a)(1)-(13)',
      'Annual contract review required per NDAC 33-03-10.1-10(7)(c)',
      'Same requirements apply to contracted staff as employees',
    ],
  },

  {
    id: 'ND-F3',
    itemId: 'F3',
    stateCode: 'ND',
    title: 'Notification of Agency Changes',
    description: 'Change notification requirements per NDAC 33-03-10.1-03(9)-(10)',
    severity: 'medium',
    regulationCode: 'NDAC 33-03-10.1-03(9)-(10)',
    regulationName: 'Agency Change Notification',
    agencyName: 'North Dakota Department of Health and Human Services',
    agencyAbbr: 'ND DHHS',
    agencyPhone: '(701) 328-2352',
    agencyUrl: 'https://www.hhs.nd.gov/health/regulation-licensure-and-certification/health-facilities-unit/home-health-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Are you aware you must notify DHHS in writing at least 30 days in advance of the following changes per NDAC 33-03-10.1-03(9)? (Select all you are aware of)',
        inputType: 'checklist',
        options: [
          { value: 'ownership', label: 'Transfer or change of ownership' },
          { value: 'operating', label: 'Transfer of operating rights' },
          { value: 'name', label: 'Change in agency name' },
          { value: 'service', label: 'Service added or deleted' },
          { value: 'geographic', label: 'Change in geographic area served' },
        ],
      },
      {
        id: 'q2',
        question: 'Are you aware changes in administrator or nurse executive must be reported within 30 days per NDAC 33-03-10.1-03(10)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Agency is aware of change notification requirements per NDAC 33-03-10.1-03(9)-(10).',
      atRisk: 'Review change notification requirements. Failure to notify can affect licensure.',
      nonCompliant: 'Agency may not be aware of required change notifications per NDAC 33-03-10.1-03(9)-(10).',
      unknown: 'Review change notification requirements per NDAC 33-03-10.1-03(9)-(10).',
    },
    stateNotes: [
      'Notify DHHS 30 days in advance for ownership, name, service, or geographic changes per NDAC 33-03-10.1-03(9)',
      'Administrator/nurse executive changes reported within 30 days per NDAC 33-03-10.1-03(10)',
      'Written notification required',
      'Failure to notify may affect licensure status',
    ],
  },
];

export default NORTH_DAKOTA_QUESTIONS;